import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiRequest, getApiUrl } from '../../config/api';
import SuccessToast from '../SuccessToast';
import ConfirmDialog from '../ConfirmDialog';

const CLAVE = /^[a-zA-Z0-9_.:-]{1,100}$/;
const TTL_MIN = 1;
const TTL_MAX = 2592000;
const TTL_POR_DEFECTO = '86400';

const textoDeError = (data, respaldo) => data?.detail || data?.error || respaldo;

const rutaLista = (botId, chatId, ambito) =>
  chatId == null
    ? `/bots/${botId}/cache-variables`
    : `/bots/${botId}/chats/${chatId}/variables/${ambito}`;

const extracto = (value) => {
  const texto = JSON.stringify(value);
  if (typeof texto !== 'string') return '';
  if (texto.length <= 80) return texto;
  return `${texto.slice(0, 80)}…`;
};

const formatearTtl = (segundos) => {
  const total = Math.max(0, Math.floor(Number(segundos)));
  const dias = Math.floor(total / 86400);
  const horas = Math.floor((total % 86400) / 3600);
  const minutos = Math.floor((total % 3600) / 60);
  const segs = total % 60;
  if (dias > 0) return horas > 0 ? `${dias} d ${horas} h` : `${dias} d`;
  if (horas > 0) return minutos > 0 ? `${horas} h ${minutos} min` : `${horas} h`;
  if (minutos > 0) return segs > 0 ? `${minutos} min ${segs} s` : `${minutos} min`;
  return `${segs} s`;
};

const jsonBonito = (value) => JSON.stringify(value, null, 2) ?? '';

function VariablesWindow({ botId, chatId, titulo, onClose, soloLectura = false }) {
  const sinChat = chatId == null;
  const [ambito, setAmbito] = useState('context');
  const [variables, setVariables] = useState(null);
  const [errorCarga, setErrorCarga] = useState(null);
  const [editor, setEditor] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [toastExito, setToastExito] = useState(null);
  const [toastError, setToastError] = useState(null);
  const [recarga, setRecarga] = useState(0);
  const [pendienteBorrar, setPendienteBorrar] = useState(null);

  const enCache = sinChat || ambito === 'cache';
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dialogoRef = useRef(null);

  useEffect(() => {
    const anterior = document.activeElement;
    const alPulsar = (event) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', alPulsar);
    dialogoRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', alPulsar);
      if (anterior instanceof HTMLElement && document.contains(anterior)) {
        anterior.focus();
      }
    };
  }, []);

  useEffect(() => {
    if (botId == null) return undefined;
    let vivo = true;
    setVariables(null);
    setErrorCarga(null);

    (async () => {
      try {
        const response = await apiRequest(getApiUrl(rutaLista(botId, chatId, ambito)));
        const data = await response.json().catch(() => ({}));
        if (!vivo) return;
        if (!response.ok) {
          setErrorCarga(textoDeError(data, 'No se pudieron cargar las variables'));
          setVariables([]);
          return;
        }
        setVariables(Array.isArray(data) ? data : []);
      } catch {
        if (!vivo) return;
        setErrorCarga('No se pudieron cargar las variables');
        setVariables([]);
      }
    })();

    return () => {
      vivo = false;
    };
  }, [botId, chatId, ambito, recarga]);

  const mostrarExito = (mensaje) => {
    setToastError(null);
    setToastExito(mensaje);
  };

  const mostrarError = (mensaje) => {
    setToastExito(null);
    setToastError(mensaje);
  };

  const abrirCrear = () => {
    setEditor({
      modo: 'crear',
      key: '',
      valor: '',
      ttl: TTL_POR_DEFECTO,
      version: 0,
      error: null,
    });
  };

  const abrirEditar = (variable) => {
    setEditor({
      modo: 'editar',
      key: variable.key,
      valor: jsonBonito(variable.value),
      ttl: variable.ttlSeconds == null ? TTL_POR_DEFECTO : String(variable.ttlSeconds),
      version: variable.version,
      error: null,
    });
  };

  const guardar = async (event) => {
    event.preventDefault();
    if (!editor || guardando) return;
    const clave = editor.key.trim();
    if (!CLAVE.test(clave)) {
      setEditor((prev) => ({ ...prev, error: 'La clave no es válida' }));
      return;
    }
    let value;
    try {
      value = JSON.parse(editor.valor);
    } catch {
      setEditor((prev) => ({ ...prev, error: 'El valor no es JSON válido' }));
      return;
    }
    let ttlSeconds = null;
    if (enCache) {
      const ttl = Number(editor.ttl);
      if (!Number.isInteger(ttl) || ttl < TTL_MIN || ttl > TTL_MAX) {
        setEditor((prev) => ({
          ...prev,
          error: 'La caducidad debe ser un entero entre 1 y 2592000',
        }));
        return;
      }
      ttlSeconds = ttl;
    }
    setGuardando(true);
    try {
      const response = await apiRequest(
        getApiUrl(`${rutaLista(botId, chatId, ambito)}/${encodeURIComponent(clave)}`),
        {
          method: 'PUT',
          body: JSON.stringify({ value, version: editor.version, ttlSeconds }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (response.status === 409) {
        setEditor((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            valor: jsonBonito(data.currentValue),
            version: data.currentVersion,
            error: null,
          };
        });
        mostrarError('La variable cambió mientras la editabas');
        return;
      }
      if (!response.ok) {
        mostrarError(textoDeError(data, 'No se pudo guardar'));
        return;
      }
      setEditor(null);
      mostrarExito('Variable guardada');
      setRecarga((n) => n + 1);
    } catch {
      mostrarError('Error de conexión');
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async (clave) => {
    setPendienteBorrar(null);
    try {
      const response = await apiRequest(
        getApiUrl(`${rutaLista(botId, chatId, ambito)}/${encodeURIComponent(clave)}`),
        { method: 'DELETE' }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        mostrarError(textoDeError(data, 'No se pudo borrar'));
        return;
      }
      mostrarExito('Variable borrada');
      setRecarga((n) => n + 1);
    } catch {
      mostrarError('Error de conexión');
    }
  };

  const vaciar = async () => {
    setPendienteBorrar(null);
    try {
      const response = await apiRequest(getApiUrl(rutaLista(botId, chatId, ambito)), {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        mostrarError(textoDeError(data, 'No se pudo borrar'));
        return;
      }
      mostrarExito('Variables borradas');
      setRecarga((n) => n + 1);
    } catch {
      mostrarError('Error de conexión');
    }
  };

  const pestanas = sinChat
    ? [{ id: 'bot', etiqueta: 'Caché del bot' }]
    : [
        { id: 'context', etiqueta: 'Contexto' },
        { id: 'cache', etiqueta: 'Caché' },
      ];

  const cargando = variables === null && !errorCarga;

  return createPortal(
    <div className="modal-overlay">
      <div
        ref={dialogoRef}
        className="modal-content vars-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vars-titulo"
        tabIndex={-1}
      >
        <SuccessToast message={toastExito} onDismiss={() => setToastExito(null)} />
        <SuccessToast message={toastError} variant="error" onDismiss={() => setToastError(null)} />
        {pendienteBorrar && (
          <ConfirmDialog
            title={pendienteBorrar.todas ? 'Borrar variables' : 'Borrar variable'}
            confirmLabel={pendienteBorrar.todas ? 'Sí, borrar todas' : 'Sí, borrar'}
            note="Esta acción no se puede deshacer."
            onCancel={() => setPendienteBorrar(null)}
            onConfirm={() => (pendienteBorrar.todas ? vaciar() : borrar(pendienteBorrar.clave))}
          >
            {pendienteBorrar.todas ? (
              '¿Borrar todas las variables de esta pestaña?'
            ) : (
              <>¿Borrar la variable <strong>{pendienteBorrar.clave}</strong>?</>
            )}
          </ConfirmDialog>
        )}
        <div className="modal-header">
          <h2 id="vars-titulo">{titulo}</h2>
          <button type="button" className="admin-btn secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>
        <div className="vars-cuerpo">
          <div className="config-tabs" role="tablist" aria-label="Ámbitos">
            {pestanas.map((pestana) => {
              const activa = sinChat || ambito === pestana.id;
              return (
                <button
                  key={pestana.id}
                  type="button"
                  role="tab"
                  aria-selected={activa}
                  className={activa ? 'config-tab active' : 'config-tab'}
                  onClick={() => {
                    if (sinChat || pestana.id === ambito) return;
                    setAmbito(pestana.id);
                    setEditor(null);
                  }}
                >
                  {pestana.etiqueta}
                </button>
              );
            })}
          </div>

          {soloLectura ? null : (
            <div className="vars-toolbar">
              <button type="button" className="admin-btn primary" onClick={abrirCrear}>
                Nueva variable
              </button>
              <button type="button" className="admin-btn danger" onClick={() => setPendienteBorrar({ todas: true })}>
                Vaciar
              </button>
            </div>
          )}

          {cargando ? <p className="config-card-hint">Cargando variables…</p> : null}
          {errorCarga ? <p className="vars-aviso">{errorCarga}</p> : null}
          {!errorCarga && variables && variables.length === 0 ? <p>No hay variables</p> : null}
          {!errorCarga && variables && variables.length > 0 ? (
            <div className="vars-tabla-wrap">
              <table className="vars-tabla">
                <thead>
                  <tr>
                    <th scope="col">Clave</th>
                    <th scope="col">Valor</th>
                    {enCache ? <th scope="col">Tiempo restante</th> : null}
                    {soloLectura ? null : (
                      <th scope="col">
                        <span className="vars-sr">Acciones</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {variables.map((variable) => (
                    <tr key={variable.key}>
                      <td>{variable.key}</td>
                      <td>
                        {soloLectura ? (
                          <pre className="vars-valor-completo">{jsonBonito(variable.value)}</pre>
                        ) : (
                          <code>{extracto(variable.value)}</code>
                        )}
                      </td>
                      {enCache ? <td>{formatearTtl(variable.ttlSeconds)}</td> : null}
                      {soloLectura ? null : (
                        <td className="vars-fila-acciones">
                          <button
                            type="button"
                            className="admin-btn secondary"
                            aria-label={`Editar ${variable.key}`}
                            onClick={() => abrirEditar(variable)}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            className="admin-btn danger"
                            aria-label={`Borrar ${variable.key}`}
                            onClick={() => setPendienteBorrar({ clave: variable.key })}
                          >
                            Borrar
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {editor ? (
            <form className="vars-editor" onSubmit={guardar}>
              <div className="form-group">
                <label htmlFor="var-clave">Clave</label>
                <input
                  id="var-clave"
                  type="text"
                  value={editor.key}
                  disabled={editor.modo === 'editar'}
                  autoComplete="off"
                  maxLength={100}
                  onChange={(event) =>
                    setEditor((prev) => ({ ...prev, key: event.target.value, error: null }))
                  }
                />
              </div>
              <div className="form-group">
                <label htmlFor="var-valor">Valor</label>
                <textarea
                  id="var-valor"
                  rows={6}
                  value={editor.valor}
                  spellCheck={false}
                  onChange={(event) =>
                    setEditor((prev) => ({ ...prev, valor: event.target.value, error: null }))
                  }
                />
              </div>
              {enCache ? (
                <div className="form-group">
                  <label htmlFor="var-ttl">Caducidad (segundos)</label>
                  <input
                    id="var-ttl"
                    type="number"
                    min={TTL_MIN}
                    max={TTL_MAX}
                    step={1}
                    value={editor.ttl}
                    onChange={(event) =>
                      setEditor((prev) => ({ ...prev, ttl: event.target.value, error: null }))
                    }
                  />
                </div>
              ) : null}
              {editor.error ? <p className="vars-form-error">{editor.error}</p> : null}
              <button type="submit" className="admin-btn primary" disabled={guardando}>
                {guardando ? 'Guardando…' : 'Guardar'}
              </button>
            </form>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default VariablesWindow;
